import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from './components/Navbar';

export default function WhatIsNubbiV2() {
  const [language, setLanguage] = useState('EN');

  const content = {
    EN: [
      {
        title: 'What is Nubbi?',
        text: `It’s a space where anybody (especially kids and kids at heart) can create original characters, share their stories, and visualize their creations through high-quality digital designs and custom illustrations.

In this MVP phase, we're focusing on building the digital creation experience. Physical products like plushies will come next.`
      },
      {
        title: 'What is Nubbiland?',
        text: `Nubbiland is the magical world where all Nubbi characters live.
It’s a collective universe built by our community of creators—full of enchanted forests, magical creatures, lightly heroes, shadowy villains, and wild adventures.

Every new character expands the world, making Nubbiland more vibrant, diverse, and alive.
Think of it as a digital playground of imagination—but one where the characters can jump off the screen and become something you can hold.`
      },
      {
        title: 'What is a Nubbiverse?',
        text: `A Nubbiverse is your own personal creative universe.
When you create multiple characters, stories, or themes that are connected—you’re building your own Nubbiverse. It can be a saga, a team of heroes, or a new land in Nubbiland.

Just like Marvel has the MCU, you can create your own connected universe of wonder and meaning.`
      },
      {
        title: 'Who are the Creators?',
        text: `Creators are the dreamers, artists, storytellers and builders of Nubbiland.
They are children, parents, teachers, artists, and anyone who dares to imagine something original.

At Nubbi, we honor every imagination. You don’t need to be a designer or a programmer. If you can dream it, you can create it—and we’ll help bring it to life.`
      },
      {
        title: 'How do I become a Creator?',
        text: `Becoming a creator is simple:
1. Join the platform – Create your free account.
2. Start creating – Use our tools or upload your own drawings.
3. Design your character – Choose traits, give it a name, a story, and a purpose.
4. Build your store – Share your creations, get feedback, and (soon) start earning when others fall in love with your Nubbis.

💡 Coming soon: AI tools and a creator academy to make it even easier to bring your imagination to life.`
      }
    ],
    ES: [
      {
        title: '¿Qué es Nubbi?',
        text: `Nubbi es una plataforma donde la imaginación se vuelve realidad.
Es un espacio donde cualquier persona (especialmente niños y adultos creativos) puede crear personajes originales, contar sus historias e incluso convertir sus creaciones en productos físicos como peluches, libros o figuras coleccionables.

Creemos que la creatividad es un superpoder, y nuestra misión es ayudar a cada persona a descubrir la magia que lleva dentro.`
      },
      {
        title: '¿Qué es Nubbiland?',
        text: `Nubbiland es el mundo mágico donde viven todos los personajes de Nubbi.
Es un universo colectivo construido por nuestra comunidad de creadores—lleno de bosques encantados, criaturas mágicas, héroes luminosos, villanos sombríos y aventuras increíbles.

Cada nuevo personaje expande el mundo, haciendo que Nubbiland sea más vibrante, diverso y vivo.
Piénsalo como un parque digital de la imaginación—donde los personajes pueden saltar de la pantalla al mundo real.`
      },
      {
        title: '¿Qué es un Nubbiverse?',
        text: `Un Nubbiverse es tu propio universo creativo.
Cuando creas múltiples personajes, historias o temas conectados—estás construyendo tu propio Nubbiverse. Puede ser una saga, un equipo de héroes o una nueva tierra dentro de Nubbiland.

Así como Marvel tiene su MCU, tú puedes crear tu propio universo conectado lleno de maravillas y significado.`
      },
      {
        title: '¿Quiénes son los Creadores?',
        text: `Los creadores son los soñadores, artistas, narradores y constructores de Nubbiland.
Son niños, padres, maestros, artistas y cualquier persona que se atreve a imaginar algo original.

En Nubbi, honramos cada imaginación. No necesitas ser diseñador ni programador. Si puedes soñarlo, puedes crearlo—y nosotros te ayudamos a hacerlo realidad.`
      },
      {
        title: '¿Cómo me convierto en Creador?',
        text: `Convertirte en creador es muy sencillo:
1. Únete a la plataforma – Crea tu cuenta gratuita.
2. Comienza a crear – Usa nuestras herramientas o sube tus propios dibujos.
3. Diseña tu personaje – Elige sus rasgos, nómbralo, dale historia y propósito.
4. Abre tu tienda – Comparte tus creaciones, recibe retroalimentación y (pronto) comienza a ganar cuando otros se enamoren de tus Nubbis.

💡 Muy pronto: herramientas con IA y una academia de creadores para hacer aún más fácil traer tu imaginación a la vida.`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 text-white">
      <Navbar />
      <div className="max-w-5xl mx-auto py-16 px-4">
        <div className="flex justify-end mb-12">
          <button onClick={() => setLanguage('ES')} className="text-3xl mr-4">🇪🇸</button>
          <button onClick={() => setLanguage('EN')} className="text-3xl">🇺🇸</button>
        </div>

        <motion.h1 
          className="text-5xl font-extrabold text-center mb-12 drop-shadow-lg"
          initial={{ opacity: 0, y: -30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8 }}
        >
          {content[language][0].title}
        </motion.h1>

        <motion.div 
          className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl shadow-xl p-10 text-center mb-16"
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <p className="text-lg whitespace-pre-line mb-8">{content[language][0].text}</p>
          <video 
            src="/intro.mp4" 
            controls 
            className="mx-auto rounded-2xl shadow-lg"
            style={{ maxHeight: '600px', width: '100%' }}
          />
        </motion.div>

        {content[language].slice(1).map((section, index) => (
          <motion.div 
            key={index} 
            className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl shadow-xl p-10 text-center mb-12"
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8, delay: 0.3 + index * 0.2 }}
          >
            <h2 className="text-3xl font-extrabold mb-4 drop-shadow-sm">{section.title}</h2>
            <p className="text-lg whitespace-pre-line">{section.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}